<template>
  <tr
    :key="item.id"
    class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100"
  >
    <!-- ID Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm tw-font-medium tw-text-gray-900">
      {{ item.id }}
    </td>

    <!-- Image Column -->
    <td class="tw-px-5 tw-py-3">
      <div class="tw-w-12 tw-h-12 tw-flex-shrink-0">
        <img
          v-if="item.image"
          :src="item.image"
          :alt="item.name"
          class="tw-w-12 tw-h-12 tw-rounded tw-object-contain tw-border tw-border-gray-200"
        />
        <div
          v-else
          class="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded tw-flex tw-items-center tw-justify-center tw-border tw-border-gray-200"
        >
          <icon icon="solar:calendar-bold-duotone" class="tw-text-gray-400 tw-text-lg" />
        </div>
      </div>
    </td>

    <!-- Name Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-font-medium tw-text-gray-900 tw-truncate tw-max-w-[200px]" :title="item.name">
        {{ item.name || 'Untitled Session' }}
      </div>
    </td>


    <!-- Speakers Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div v-if="item.speakers && item.speakers.length > 0" class="tw-flex tw-items-center tw-justify-center" >
        <div v-for="(speaker, index) in item.speakers.slice(0, 3)" :key="speaker.id" :class="[index > 0 ? 'tw-ml-[-10px]' : '']" class="hover:!tw-z-20 hover:tw-scale-105 tw-relative tw-group/speaker" :style="{ zIndex: item.speakers.length - index }">
          <img :src="speaker.profile_image" :alt="speaker.name" class="tw-min-w-10 tw-min-h-10 tw-max-w-10 tw-max-h-10 tw-aspect-square tw-rounded-full tw-object-cover tw-border-2 tw-border-white tw-border-solid" />
          <div class="tw-absolute tw-w-fit tw-top-0 tw-left-1/2 -tw-translate-x-1/2 tw-translate-y-[-100%] tw-bg-black tw-py-1 tw-px-2 tw-rounded tw-h-fit tw-opacity-0 group-hover/speaker:tw-opacity-100 tw-transition-opacity tw-duration-300">
            <div class="tw-flex tw-items-center tw-justify-center">
              <span class="tw-text-white tw-text-xs tw-whitespace-nowrap">{{ speaker.name }}</span>
            </div>
          </div>
        </div>
        <div v-if="item.speakers.length > 3" class="tw-flex tw-items-center tw-justify-center">
          <span class="tw-text-gray-400 tw-text-xs tw-whitespace-nowrap">+{{ item.speakers.length - 3 }}</span>
        </div>
      </div>
      <div v-else class=" tw-text-center">
        <span class="tw-text-gray-400 tw-text-xs tw-whitespace-nowrap">{{ $t('common.no-speakers', 'No speakers') }}</span>
      </div>
    </td>

    <!-- Attendees Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div v-if="item.attendees && item.attendees.length > 0" class="tw-flex tw-items-center tw-justify-center" >
        <div v-for="(attendee, index) in item.attendees.slice(0, 3)" :key="attendee.id" :class="[index > 0 ? 'tw-ml-[-10px]' : '']" class="hover:!tw-z-20 hover:tw-scale-105 tw-relative tw-group/attendee" :style="{ zIndex: item.attendees.length - index }">
          <img :src="attendee.profile_image" :alt="attendee.name" class="tw-min-w-10 tw-min-h-10 tw-max-w-10 tw-max-h-10 tw-aspect-square tw-rounded-full tw-object-cover tw-border-2 tw-border-white tw-border-solid" />
          <div class="tw-absolute tw-w-fit tw-top-0 tw-left-1/2 -tw-translate-x-1/2 tw-translate-y-[-100%] tw-bg-black tw-py-1 tw-px-2 tw-rounded tw-h-fit tw-opacity-0 group-hover/attendee:tw-opacity-100 tw-transition-opacity tw-duration-300">
            <div class="tw-flex tw-items-center tw-justify-center">
              <span class="tw-text-white tw-text-xs tw-whitespace-nowrap">{{ attendee.name }}</span>
            </div>
          </div>
        </div>
        <div v-if="item.attendees_count > 3" class="tw-flex tw-items-center tw-justify-center">
          <span class="tw-text-gray-400 tw-text-xs tw-whitespace-nowrap">+{{ item.attendees_count - 3 }}</span>
        </div>
      </div>
      <div v-else class=" tw-text-center">
        <span class="tw-text-gray-400 tw-text-xs tw-whitespace-nowrap">{{ $t('common.no-attendees', 'No attendees') }}</span>
      </div>
    </td>

    <!-- Description Column -->
    <td v-if="false" class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-text-gray-600 tw-max-w-[250px] tw-truncate" :title="item.description">
        {{ item.description || 'No description' }}
      </div>
    </td>

    <!-- Status Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-flex tw-items-center tw-gap-2">
        <!-- Active/Inactive Badge -->
        <!-- <span
          v-if="item.is_active !== undefined"
          :class="[
            item.is_active
              ? 'tw-bg-emerald-100 tw-text-emerald-800'
              : 'tw-bg-gray-100 tw-text-gray-800'
          ]"
          class="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium"
        >
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </span> -->
        
        <!-- Custom Status Badge -->
        <span
          v-if="item.status"
          :class="getStatusColor(item.status)"
          class="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium"
        >
          {{ item.status }}
        </span>
      </div>
    </td>

    <!-- Dates Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-text-gray-600">
        <div v-if="item.start_date" class="tw-text-xs tw-text-gray-500">
          Start: {{ formatDate(item.start_date) }}
        </div>
        <div v-if="item.end_date" class="tw-text-xs tw-text-gray-500 tw-mt-1">
          End: {{ formatDate(item.end_date) }}
        </div>
        <div v-if="!item.start_date && !item.end_date" class="tw-text-gray-400 tw-text-xs">
          No dates set
        </div>
      </div>
    </td>

    <!-- Type Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-text-gray-600">
        {{ item.type || '-' }}
      </div>
    </td>

    <!-- Topic Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-text-gray-600">
        {{ item.topic || '-' }}
      </div>
    </td>

    <!-- Language Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-text-gray-600">
        {{ item.language || '-' }}
      </div>
    </td>

    <!-- Link Column -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-flex tw-items-center tw-justify-center">
        <a
          v-if="item.link"
          :href="item.link"
          target="_blank"
          rel="noopener noreferrer"
          class="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-bg-blue-100 tw-text-blue-600 tw-rounded-full hover:tw-bg-blue-200 tw-transition-colors"
          :title="item.link"
        >
          <icon icon="solar:link-bold" class="tw-text-sm" />
        </a>
        <span v-else class="tw-text-gray-400 tw-text-xs">No link</span>
      </div>
    </td>

    <!-- Actions Column -->
    <td class="tw-px-5 tw-py-3">
      <AdminSessionTableActions :item="item" />
    </td>
  </tr>
</template>

<script setup>
import { defineProps, provide, toRef } from 'vue';
import AdminSessionTableActions from './AdminSessionTableActions.vue';

const props = defineProps(['item']);
const item = toRef(props, 'item');

// Provide the session item to child components
provide('session', item);

// Helper functions
const formatDate = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    
    // Check if the date string has a timezone indicator (Z suffix)
    const isUTC = typeof date === 'string' && date.includes('Z');
    
    let month, day, year, hours, minutes;
    
    if (isUTC) {
      // For UTC dates, use UTC methods to avoid timezone conversion
      month = dateObj.toLocaleString('en-US', { month: 'short' });
      day = dateObj.getUTCDate();
      year = dateObj.getUTCFullYear();
      hours = dateObj.getUTCHours();
      minutes = dateObj.getUTCMinutes();
    } else {
      // For local dates, use local methods
      month = dateObj.toLocaleString('en-US', { month: 'short' });
      day = dateObj.getDate();
      year = dateObj.getFullYear();
      hours = dateObj.getHours();
      minutes = dateObj.getMinutes();
    }
    
    // Format time in 12-hour format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  } catch (error) {
    return date;
  }
};

const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  
  switch (statusLower) {
    case 'upcoming':
    case 'scheduled':
      return 'tw-bg-blue-100 tw-text-blue-800';
    case 'live':
    case 'ongoing':
      return 'tw-bg-green-100 tw-text-green-800';
    case 'completed':
    case 'finished':
      return 'tw-bg-gray-100 tw-text-gray-800';
    case 'cancelled':
      return 'tw-bg-red-100 tw-text-red-800';
    case 'draft':
      return 'tw-bg-yellow-100 tw-text-yellow-800';
    default:
      return 'tw-bg-gray-100 tw-text-gray-800';
  }
};

</script>

<style scoped>
/* Add any custom styles here */
</style> 